/**
 * Draft state + undo/redo for the abstract floorplan editor.
 *
 * History semantics: discrete actions (add/update/remove/clear) push one
 * undo entry each. Continuous gestures (dragging a station, sizing a zone)
 * call `beginGesture` once on pointer-down, then any number of `preview*`
 * actions that mutate the present without touching history — so a whole
 * drag undoes in one step.
 */
import { useCallback, useMemo, useReducer } from 'react';
import type { PlanFloorplan } from '../../../types/ReunificationTypes';
import type {
  DraftFlow,
  DraftStation,
  DraftZone,
  FloorplanDraft,
} from './types';
import { emptyDraft } from './types';

const HISTORY_LIMIT = 50;

let tmpCounter = 0;
export const nextTmpCid = () => `tmp-${++tmpCounter}`;

export function draftFromFloorplan(floorplan: PlanFloorplan): FloorplanDraft {
  const stations: DraftStation[] = floorplan.stations.map((s) => ({
    cid: s.public_id,
    publicId: s.public_id,
    name: s.name,
    description: s.description,
    station_type: s.station_type,
    canvas_x: s.canvas_x,
    canvas_y: s.canvas_y,
    min_staff: s.min_staff,
    recommended_staff: s.recommended_staff,
    staff_ratio_per_students: s.staff_ratio_per_students,
    capacity: s.capacity,
    requires_equipment: [...s.requires_equipment],
    is_required: s.is_required,
    sort_order: s.sort_order,
  }));
  const zones: DraftZone[] = floorplan.zones.map((z) => ({
    cid: z.public_id,
    publicId: z.public_id,
    name: z.name,
    description: z.description,
    zone_type: z.zone_type,
    canvas_x: z.canvas_x,
    canvas_y: z.canvas_y,
    canvas_width: z.canvas_width,
    canvas_height: z.canvas_height,
    capacity: z.capacity,
    color: z.color,
  }));
  const flows: DraftFlow[] = floorplan.flows
    .filter(
      (f) => (f.from_station || f.from_zone) && (f.to_station || f.to_zone),
    )
    .map((f) => ({
      cid: f.public_id,
      publicId: f.public_id,
      flow_type: f.flow_type,
      from: f.from_station
        ? { kind: 'station' as const, cid: f.from_station }
        : { kind: 'zone' as const, cid: f.from_zone as string },
      to: f.to_station
        ? { kind: 'station' as const, cid: f.to_station }
        : { kind: 'zone' as const, cid: f.to_zone as string },
      description: f.description,
      is_primary_path: f.is_primary_path,
      sequence_order: f.sequence_order,
    }));
  return { stations, zones, flows };
}

interface HistoryState {
  past: FloorplanDraft[];
  present: FloorplanDraft;
  future: FloorplanDraft[];
  /** Snapshot of the draft as loaded from (or last synced with) the server. */
  baseline: FloorplanDraft;
}

type Action =
  | { type: 'load'; draft: FloorplanDraft }
  | { type: 'mark_saved'; draft: FloorplanDraft }
  | { type: 'add_station'; station: DraftStation }
  | { type: 'update_station'; cid: string; patch: Partial<DraftStation> }
  | { type: 'remove_station'; cid: string }
  | { type: 'add_zone'; zone: DraftZone }
  | { type: 'update_zone'; cid: string; patch: Partial<DraftZone> }
  | { type: 'remove_zone'; cid: string }
  | { type: 'add_flow'; flow: DraftFlow }
  | { type: 'update_flow'; cid: string; patch: Partial<DraftFlow> }
  | { type: 'remove_flow'; cid: string }
  | { type: 'clear' }
  | { type: 'begin_gesture' }
  | { type: 'preview_station_move'; cid: string; x: number; y: number }
  | {
      type: 'preview_zone_rect';
      cid: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }
  | { type: 'undo' }
  | { type: 'redo' };

function pushHistory(state: HistoryState, next: FloorplanDraft): HistoryState {
  return {
    ...state,
    past: [...state.past.slice(-(HISTORY_LIMIT - 1)), state.present],
    present: next,
    future: [],
  };
}

/** Mutate the present without recording history (mid-gesture previews). */
function replacePresent(
  state: HistoryState,
  next: FloorplanDraft,
): HistoryState {
  return { ...state, present: next };
}

function withoutEntity(
  draft: FloorplanDraft,
  kind: 'station' | 'zone',
  cid: string,
): FloorplanDraft {
  return {
    stations:
      kind === 'station'
        ? draft.stations.filter((s) => s.cid !== cid)
        : draft.stations,
    zones:
      kind === 'zone' ? draft.zones.filter((z) => z.cid !== cid) : draft.zones,
    // Cascade: drop flows touching the removed endpoint.
    flows: draft.flows.filter(
      (f) =>
        !(f.from.kind === kind && f.from.cid === cid) &&
        !(f.to.kind === kind && f.to.cid === cid),
    ),
  };
}

function reducer(state: HistoryState, action: Action): HistoryState {
  const { present } = state;
  switch (action.type) {
    case 'load':
      return {
        past: [],
        present: action.draft,
        future: [],
        baseline: action.draft,
      };
    case 'mark_saved':
      return { ...state, present: action.draft, baseline: action.draft };
    case 'add_station':
      return pushHistory(state, {
        ...present,
        stations: [...present.stations, action.station],
      });
    case 'update_station':
      return pushHistory(state, {
        ...present,
        stations: present.stations.map((s) =>
          s.cid === action.cid ? { ...s, ...action.patch } : s,
        ),
      });
    case 'remove_station':
      return pushHistory(state, withoutEntity(present, 'station', action.cid));
    case 'add_zone':
      return pushHistory(state, {
        ...present,
        zones: [...present.zones, action.zone],
      });
    case 'update_zone':
      return pushHistory(state, {
        ...present,
        zones: present.zones.map((z) =>
          z.cid === action.cid ? { ...z, ...action.patch } : z,
        ),
      });
    case 'remove_zone':
      return pushHistory(state, withoutEntity(present, 'zone', action.cid));
    case 'add_flow':
      return pushHistory(state, {
        ...present,
        flows: [...present.flows, action.flow],
      });
    case 'update_flow':
      return pushHistory(state, {
        ...present,
        flows: present.flows.map((f) =>
          f.cid === action.cid ? { ...f, ...action.patch } : f,
        ),
      });
    case 'remove_flow':
      return pushHistory(state, {
        ...present,
        flows: present.flows.filter((f) => f.cid !== action.cid),
      });
    case 'clear':
      return pushHistory(state, emptyDraft());
    case 'begin_gesture':
      return pushHistory(state, present);
    case 'preview_station_move':
      return replacePresent(state, {
        ...present,
        stations: present.stations.map((s) =>
          s.cid === action.cid
            ? { ...s, canvas_x: action.x, canvas_y: action.y }
            : s,
        ),
      });
    case 'preview_zone_rect':
      return replacePresent(state, {
        ...present,
        zones: present.zones.map((z) =>
          z.cid === action.cid
            ? {
                ...z,
                canvas_x: action.x,
                canvas_y: action.y,
                canvas_width: action.width,
                canvas_height: action.height,
              }
            : z,
        ),
      });
    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...state,
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future],
      };
    }
    case 'redo': {
      const [next, ...rest] = state.future;
      if (!next) return state;
      return {
        ...state,
        past: [...state.past, state.present],
        present: next,
        future: rest,
      };
    }
    default:
      return state;
  }
}

export function useFloorplanDraft() {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    past: [],
    present: emptyDraft(),
    future: [],
    baseline: emptyDraft(),
  }));

  const isDirty = useMemo(
    () => JSON.stringify(state.present) !== JSON.stringify(state.baseline),
    [state.present, state.baseline],
  );

  const load = useCallback(
    (floorplan: PlanFloorplan) =>
      dispatch({ type: 'load', draft: draftFromFloorplan(floorplan) }),
    [],
  );

  return {
    draft: state.present,
    baseline: state.baseline,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    isDirty,
    load,
    dispatch,
  };
}

export type FloorplanDraftApi = ReturnType<typeof useFloorplanDraft>;
export type DraftAction = Action;
